import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { BaseComponent } from '../../core/base.component';
import { NotificationService } from '../../notification/notification.service';
import { AuthService } from '../../core/security/auth.service';
import { ContactService } from '../contact.service';
import { RolesEnum } from '../../core/security/roles.enum';
import { ContactSyncModel } from '../../api/models/contact-sync-model';
import { FileHelpers } from '../../core/utilities/file.helpers';
import { FileUploadModel } from '../../api/models/file-upload-model';
import { MatDialog } from '@angular/material/dialog';
import { ContactsImportResultsDialogComponent } from './contacts-import-results-dialog.component';
import { CustomFieldsService } from '../../api/services';
import { BehaviorSubject, take } from 'rxjs';
import { CustomFieldModel } from '../../api/models';
import { FlexModule } from '@angular/flex-layout';
import { NgFor, AsyncPipe } from '@angular/common';
import { ContactSingleIntegrationComponent } from './contact-single-integration/contact-single-integration.component';
import { MatCard, MatCardTitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { MessagesModule } from 'primeng/messages';
import { PrimeTemplate } from 'primeng/api';
import { DroppableDirective } from '../../core/visual/droppable/droppable.directive';
import { MatLabel } from '@angular/material/form-field';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { Message } from 'primeng/message';

@Component({
  selector: 'app-contact-integration',
  templateUrl: './contact-integration.component.html',
  styleUrls: ['./contact-integration.component.scss'],
  standalone: true,
  imports: [
    FlexModule,
    NgFor,
    ContactSingleIntegrationComponent,
    MatCard,
    MatCardTitle,
    MatCardContent,
    MessagesModule,
    DroppableDirective,
    MatLabel,
    MatRadioGroup,
    FormsModule,
    MatRadioButton,
    MatCheckbox,
    MatCardActions,
    MatButton,
    MatTooltip,
    Angular2PromiseButtonModule,
    AsyncPipe,
    Message,
  ],
})
export class ContactIntegrationComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  importCsvError: string = null;
  submitted: boolean;
  serverError: boolean;
  settingsModel: ContactSyncModel[] = [];
  filesToUpload: File[] = [];
  buttonImportPromise: Promise<void>;
  buttonSubmitPromise: Promise<void>;

  customFieldData$ = new BehaviorSubject<CustomFieldModel[]>([]);

  importMergeOptions = {
    importMergeType: 'all',
    importMergeByMobilePhone: true,
    importMergeByEmailAddress: true,
    importSkipByMobilePhone: true,
    importSkipByEmailAddress: true,
  };

  @ViewChild('fileInput', { static: false }) fileInput: any;
  public id: string;

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    public dialog: MatDialog,
    public contactService: ContactService,
    private notificationService: NotificationService,
    private readonly customFieldsService: CustomFieldsService,
  ) {
    super(route, headerService, titleService, authService);

    this.viewRoles = [];
    this.editRoles = [
      RolesEnum.CompanyContact_Edit,
      RolesEnum.CompanyContact_Delete,
      RolesEnum.PrivateContact_Edit,
      RolesEnum.PrivateContact_Delete,
    ];
    this.deleteRoles = [];

    this.headerService.showBackButton(true);
  }

  async ngOnInit() {
    super.ngOnInit();
    this.loadCustomFieldsData();
    this.ngOnChanges();
  }

  ngOnChanges(changes?: SimpleChanges): void {
    console.log('ngOnChanges');
    this.refreshSettings();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.headerService.showBackButton(false);
  }

  refreshSettings() {
    this.contactService.getIntegrationSettings().then(data => {
      this.settingsModel = data;
    });
  }

  onImportOptionsChange($event: any) {
    if (this.importMergeOptions.importMergeType !== 'skip') {
      // this.importMergeOptions.importSkipByMobilePhone
    }
  }

  async processImport(): Promise<void> {
    const data: FileUploadModel = {
      importMergeType: this.importMergeOptions.importMergeType,
      file: undefined,
      importMergeByEmailAddress: false,
      importMergeByMobilePhone: false,
    };

    if (this.importMergeOptions.importMergeType === 'skip') {
      data.importMergeByMobilePhone = this.importMergeOptions.importSkipByMobilePhone;
      data.importMergeByEmailAddress = this.importMergeOptions.importSkipByEmailAddress;
    } else if (this.importMergeOptions.importMergeType === 'append') {
      data.importMergeByMobilePhone = this.importMergeOptions.importMergeByMobilePhone;
      data.importMergeByEmailAddress = this.importMergeOptions.importMergeByEmailAddress;
    }

    try {
      const requestSucceeded = await this.contactService.importContacts(data, this.filesToUpload[0]);

      if (requestSucceeded) {
        this.dialog.open(ContactsImportResultsDialogComponent, {
          width: '650px',
          data: requestSucceeded,
        });

        this.notificationService.notifySuccess('Import successfully submitted');
        this.importCsvError = null;

        this.removeAttachment();
      }
    } catch (e: any) {
      this.importCsvError = e?.error?.message || 'Unknown CSV Import Error';
    }
  }

  onImport($event: MouseEvent) {
    this.buttonImportPromise = this.processImport();
  }

  removeAttachment() {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }

    this.filesToUpload = [];
  }

  validateAndProceedFile(file: File) {
    if (FileHelpers.isSupportedFile(file, 'csv')) {
      this.filesToUpload = [file];
    } else {
      this.notificationService.notifyDanger('Unsupported file type. Please upload a CSV file.');
      this.removeAttachment();
    }
  }

  fileChangeEvent(fileInput: any) {
    const files = fileInput.target.files;
    if (files.length > 0) {
      const file: File = files[0];
      this.validateAndProceedFile(file);
    }
  }

  dropFile(event: DragEvent) {
    console.log('drop event');

    const files = event.dataTransfer.files;

    if (files.length > 0) {
      const file: File = files[0];
      this.validateAndProceedFile(file);
    }
  }

  integrationChanged($event: ContactSyncModel) {
    this.refreshSettings();
    this.loadCustomFieldsData();
  }

  private loadCustomFieldsData(): void {
    this.customFieldsService
      .customFieldsGetCustomFieldList({
        body: {
          sort: [{ field: 'fieldOrder', direction: 'DESC' }],
          limit: 500,
        },
      })
      .pipe(take(1))
      .subscribe(data => {
        this.customFieldData$.next(data?.data || []);
      });
  }
}
